/**
 * Sparkline — gráfico mini inline. SVG puro, sem libs.
 */
import { cn } from "@/lib/utils";

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  className?: string;
  color?: "primary" | "success" | "warning" | "danger" | "purple";
  filled?: boolean;
}

const STROKE_COLOR = {
  primary: "#0ea5e9",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  purple: "#8b5cf6",
};

const FILL_COLOR = {
  primary: "rgba(14, 165, 233, 0.12)",
  success: "rgba(16, 185, 129, 0.12)",
  warning: "rgba(245, 158, 11, 0.12)",
  danger: "rgba(239, 68, 68, 0.12)",
  purple: "rgba(139, 92, 246, 0.12)",
};

export function Sparkline({
  data,
  width = 90,
  height = 28,
  className,
  color = "primary",
  filled = true,
}: SparklineProps) {
  if (data.length === 0) {
    return <div className={cn("inline-block", className)} style={{ width, height }} />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * height;
    return [x, y] as const;
  });

  const pathLine = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(" ");

  const pathArea = `${pathLine} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn("inline-block overflow-visible", className)}
      preserveAspectRatio="none"
    >
      {filled && <path d={pathArea} fill={FILL_COLOR[color]} />}
      <path
        d={pathLine}
        fill="none"
        stroke={STROKE_COLOR[color]}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.length > 0 && (
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r={2.5}
          fill={STROKE_COLOR[color]}
        />
      )}
    </svg>
  );
}
